import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Mic, Square, PlayCircle, Volume2, Loader2, ArrowLeft, Check, Play, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { WordPlayButton } from '../components/PronunciationTips';
import { SOUND_LESSONS } from '../lib/soundLessons';
import { useAuth } from '../contexts/AuthContext';
import { settingsApi, premadeApi } from '../lib/api';
import { AudioRecorder, getMicPermissionState, measureLoudness, IS_IOS } from '../lib/speech';
import { reportError } from '../lib/analytics';
import { trackPlayback } from '../lib/practiceTime';
import { speakFrench, cancelFrench, getSavedTTSVoice, unlockAudioPlayback } from '../lib/tts';

const GAME_ROUNDS = 5;
// "Hear the difference." and "Test your ears" share one style (Leanne).
const SUBHEAD = 'text-[15px] font-semibold text-foreground';
const PLAY_ALL_GAP_MS = 500;

// Plays a list of words one after another, in the learner's voice. Tap again
// to stop. Only one list plays at a time: starting one stops any other.
let playAllRun = 0;
function PlayAll({ words, label = 'Play all' }) {
    const [playing, setPlaying] = useState(false);
    const run = useRef(0);
    useEffect(() => () => { if (run.current === playAllRun) { playAllRun++; cancelFrench(); } }, []);
    const toggle = async () => {
        if (playing) { playAllRun++; cancelFrench(); setPlaying(false); return; }
        unlockAudioPlayback();
        const mine = ++playAllRun;
        run.current = mine;
        setPlaying(true);
        for (const w of words) {
            if (playAllRun !== mine) break;
            cancelFrench();
            try { await speakFrench(w, 1.0, getSavedTTSVoice(), null, true); } catch { /* nicety */ }
            if (playAllRun !== mine) break;
            await new Promise(r => setTimeout(r, PLAY_ALL_GAP_MS));
        }
        if (playAllRun === mine) setPlaying(false);
    };
    useEffect(() => {
        if (!playing) return undefined;
        const t = setInterval(() => { if (run.current !== playAllRun) setPlaying(false); }, 300);
        return () => clearInterval(t);
    }, [playing]);
    return (
        <button type="button" onClick={toggle}
                className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/5 px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/10">
            {playing ? <Square className="h-3 w-3" /> : <Volume2 className="h-3.5 w-3.5" />}
            {playing ? 'Stop' : label}
        </button>
    );
}

function Section({ step, title, subtitle, children }) {
    return (
        <Card className="bg-white border-stone-100 shadow-sm mb-4">
            <CardContent className="p-4 md:p-6">
                <h2 className={`text-lg font-semibold text-primary ${subtitle ? '' : 'mb-3'}`}>{step}. {title}</h2>
                {subtitle && <p className={`${SUBHEAD} mb-3`}>{subtitle}</p>}
                {children}
            </CardContent>
        </Card>
    );
}

// A word, its phonetics underneath, and the play button the word cards use,
// so it is said in the voice the learner picked.
function Word({ word, phonetic }) {
    return (
        <span className="inline-flex items-center gap-1 rounded-lg border border-stone-200 bg-stone-50 pl-3 pr-1 py-1">
            <span className="flex flex-col leading-tight">
                <span className="text-sm font-medium text-foreground">{word}</span>
                {phonetic && <span className="text-[11px] text-stone-500">{phonetic}</span>}
            </span>
            <WordPlayButton word={word} />
        </span>
    );
}

const say = async (word) => {
    unlockAudioPlayback();
    cancelFrench();
    try { await speakFrench(word, 1.0, getSavedTTSVoice(), null, true); } catch { /* nicety */ }
};

// "Test your ears": tap Go, a word from a pair plays, pick which one it was.
// After each answer it shows right or wrong for a moment, then moves on and
// plays the next word by itself (Leanne, 25 Oct 2026: more automatic).
const GAME_PAUSE_MS = 1300;

function ListeningGame({ pairs }) {
    const pick = () => {
        const pair = pairs[Math.floor(Math.random() * pairs.length)];
        return { pair, answer: pair[Math.floor(Math.random() * pair.length)] };
    };
    const [started, setStarted] = useState(false);
    const [round, setRound] = useState(0);
    const [q, setQ] = useState(pick);
    const [chosen, setChosen] = useState(null);
    const [right, setRight] = useState(0);
    const timer = useRef(null);
    const done = round >= GAME_ROUNDS;

    useEffect(() => () => clearTimeout(timer.current), []);

    const begin = () => {
        const first = pick();
        setStarted(true); setRound(0); setRight(0); setChosen(null); setQ(first);
        say(first.answer);
    };
    const answer = (w) => {
        if (chosen) return;
        setChosen(w);
        if (w === q.answer) setRight(n => n + 1);
        timer.current = setTimeout(() => {
            const next = round + 1;
            setRound(next);
            setChosen(null);
            if (next < GAME_ROUNDS) { const nq = pick(); setQ(nq); say(nq.answer); }
        }, GAME_PAUSE_MS);
    };

    return (
        <div className="rounded-xl bg-stone-50 border border-stone-100 p-4 text-center" data-testid="lesson-game">
            <p className={`${SUBHEAD} mb-1`}>Test your ears</p>
            {!started ? (
                <>
                    <p className="text-xs text-stone-500 mb-4">Listen to {GAME_ROUNDS} words. Pick the one you hear.</p>
                    <Button className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground px-8" onClick={begin}>Go</Button>
                </>
            ) : done ? (
                <div data-testid="lesson-game-done">
                    <p className="text-2xl font-bold text-foreground my-2">{right} / {GAME_ROUNDS}</p>
                    <Button variant="outline" className="rounded-full" onClick={begin}>Try again</Button>
                </div>
            ) : (
                <>
                    <div className="flex justify-center gap-1.5 my-3" aria-hidden="true">
                        {Array.from({ length: GAME_ROUNDS }, (_, i) => (
                            <span key={i} className={`h-1.5 w-6 rounded-full ${i < round ? 'bg-primary' : i === round ? 'bg-primary/50' : 'bg-stone-200'}`} />
                        ))}
                    </div>
                    <div className={`grid ${q.pair.length > 2 ? 'grid-cols-3 max-w-sm' : 'grid-cols-2 max-w-xs'} gap-3 mx-auto`}>
                        {q.pair.map(w => {
                            const tone = !chosen ? 'border-stone-300 bg-white hover:border-primary'
                                : w === q.answer ? 'border-emerald-400 bg-emerald-50 text-emerald-800'
                                    : w === chosen ? 'border-rose-300 bg-rose-50 text-rose-700' : 'border-stone-200 bg-white text-stone-400';
                            return (
                                <button key={w} type="button" onClick={() => answer(w)}
                                        className={`rounded-xl border-2 py-3 text-base font-semibold transition-colors ${tone}`}>
                                    {w}
                                </button>
                            );
                        })}
                    </div>
                    <p className={`h-5 mt-3 text-sm font-medium ${!chosen ? 'text-stone-400' : chosen === q.answer ? 'text-emerald-700' : 'text-rose-600'}`}>
                        {!chosen ? '' : chosen === q.answer ? 'Right!' : `It was "${q.answer}"`}
                    </p>
                    <button type="button" onClick={() => say(q.answer)} disabled={!!chosen}
                            className="inline-flex items-center gap-1 text-xs text-primary font-medium mt-1 disabled:opacity-40">
                        <Volume2 className="h-3.5 w-3.5" /> Hear it again
                    </button>
                </>
            )}
        </div>
    );
}

// Say each word and hear yourself back next to the native voice. Not scored:
// a word scored on its own is an easier test than the word in a sentence, so
// the number meant nothing (see WordVoiceButton). The phrases are scored.
// Each pair on one row, side by side, so the difference is obvious.
function RepeatCompare({ pairs, phon }) {
    const recRef = useRef(null);
    const [recording, setRecording] = useState(null);   // the word being recorded
    const [mine, setMine] = useState({});               // word -> object URL
    const audioRef = useRef(null);
    // ONE mic for the whole page, opened once and reused for every word, as
    // Manual and Automatic do. Opening it fresh per word gave pure silence on
    // Leanne's laptop (rms 0, 25 Oct 2026): that mic sends zeros for a moment
    // after it opens, and a single word fits inside that gap.
    const micRef = useRef(null);
    const micLive = () => !!micRef.current?.stream?.getTracks().some(t => t.readyState === 'live');
    const openMic = async () => {
        if (micLive()) return true;
        const rec = new AudioRecorder();
        const ok = await rec.preWarm();
        if (ok !== true) { rec.cleanup(); return false; }
        micRef.current = rec;
        return true;
    };

    useEffect(() => {
        // Open it ahead of the first tap, but only when permission was already
        // given: the permission popup belongs to a tap on record.
        getMicPermissionState().then(state => { if (state === 'granted') openMic(); }).catch(() => {});
        return () => {
            recRef.current?.cleanup();
            audioRef.current?.pause();
            micRef.current?.stream?.getTracks().forEach(t => t.stop());
            micRef.current = null;
        };
    }, []);  // eslint-disable-line react-hooks/exhaustive-deps

    const start = async (word) => {
        if (recording) return;
        cancelFrench();
        if (!(await openMic())) { toast.error('Could not use the microphone.'); return; }
        const rec = new AudioRecorder();
        rec.useStream(micRef.current.stream);
        if (!rec.startFromStream()) { toast.error('Could not use the microphone.'); return; }
        recRef.current = rec;
        setRecording(word);
    };
    const stop = async () => {
        const rec = recRef.current;
        if (!rec) return;
        const word = recording;
        const res = await rec.stop();
        rec.cleanup();
        recRef.current = null;
        setRecording(null);
        // Words here are not scored, so nothing on the server can say a take
        // came back empty, as it does in Manual and Automatic. Check it here:
        // an empty or all-zero clip is not kept, the learner is asked to go
        // again, and it is reported so the cause can be found (Susan, 4 Nov
        // 2026: some words "didn't capture" on her iPhone). Quiet is not
        // silent: iOS records far lower, so only true silence counts.
        const blob = res?.blob;
        const loud = blob?.size ? await measureLoudness(blob) : { rms: null, peak: null };
        const silent = !blob?.size || (loud.peak != null && loud.peak < 0.002);
        if (silent) {
            reportError('recording_silent', {
                error_type: 'no_speech_detected', mode: 'sound_lesson', word,
                clip_bytes: blob?.size || 0, clip_mime: res?.mimeType || '',
                loudness_rms: loud.rms, loudness_peak: loud.peak, is_ios: IS_IOS,
            });
            toast.error("I didn't catch that one. Tap record and say it again.");
            return;
        }
        // Our own URL from the blob: cleanup() revokes the recorder's, which
        // left the play button with nothing to play (Leanne, 25 Oct 2026).
        setMine(m => ({ ...m, [word]: URL.createObjectURL(blob) }));
    };
    // Green only while it plays, grey again after (Leanne, 25 Oct 2026).
    // Tapping it while it plays stops it.
    const [playingMine, setPlayingMine] = useState(null);
    const playMine = (word) => {
        audioRef.current?.pause();
        if (playingMine === word) { setPlayingMine(null); return; }
        cancelFrench();
        const a = new Audio(mine[word]);
        audioRef.current = a;
        const done = () => setPlayingMine(w => (w === word ? null : w));
        a.onended = done;
        a.onpause = done;
        setPlayingMine(word);
        trackPlayback(a);   // listening to your own voice is practice too
        a.play().catch(() => { done(); toast.error('Could not play your recording.'); });
    };

    const cell = (word) => (
        <div className="flex flex-1 min-w-0 items-center gap-1 rounded-lg border border-stone-200 bg-white pl-2.5 pr-1 py-1.5">
            <span className="flex-1 min-w-0 leading-tight">
                <span className="block text-sm font-medium text-foreground">{word}</span>
                {phon[word] && <span className="block text-[11px] text-stone-500">{phon[word]}</span>}
            </span>
            <WordPlayButton word={word} />
            {recording === word ? (
                <button type="button" onClick={stop} aria-label="Stop"
                        className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-rose-500 text-white animate-pulse">
                    <Square className="h-3 w-3" />
                </button>
            ) : (
                <button type="button" onClick={() => start(word)} aria-label={`Record ${word}`} disabled={!!recording}
                        className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-accent text-accent-foreground disabled:opacity-40">
                    <Mic className="h-3 w-3" />
                </button>
            )}
            <button type="button" onClick={() => playMine(word)} aria-label={`Play your ${word}`} disabled={!mine[word]}
                    className={`inline-flex items-center justify-center w-7 h-7 rounded-full disabled:opacity-30 ${
                        playingMine === word ? 'bg-primary text-white' : 'text-stone-500'}`}>
                <PlayCircle className="h-4 w-4" />
            </button>
        </div>
    );

    return (
        <div className="space-y-2">
            <p className={SUBHEAD}>Listen, record &amp; compare.</p>
            {/* Phones: each pair stacked in its own box, ou above u, so nothing
                is squeezed. Wider screens: side by side. */}
            {pairs.map(set => (
                <div key={set[0]} className="flex flex-col sm:flex-row gap-1.5 sm:gap-2 rounded-xl bg-stone-50 p-1.5 sm:bg-transparent sm:p-0">
                    {set.map(w => <span key={w} className="contents">{cell(w)}</span>)}
                </div>
            ))}
        </div>
    );
}

export default function SoundLesson() {
    const { id } = useParams();
    const navigate = useNavigate();
    const lesson = SOUND_LESSONS[id];
    const { user } = useAuth();
    const [style, setStyle] = useState('english_syllables');
    const [opening, setOpening] = useState(false);
    const [added, setAdded] = useState(false);

    useEffect(() => {
        settingsApi.get().then(res => setStyle(res.data?.phonetic_style || 'english_syllables')).catch(() => {});
        if (lesson) {
            premadeApi.list()
                .then(res => setAdded(!!(res.data.topics || []).find(t => t.id === lesson.drillTopic)?.added))
                .catch(() => {});
        }
    }, [lesson]);

    if (!lesson || !user?.sound_lessons) {
        return <div className="p-8 text-center text-stone-500">Lesson not found.</div>;
    }
    const phon = lesson.phonetics[style] || lesson.phonetics.english_syllables;

    // Practising does NOT add the drill to their phrases (Leanne, 25 Oct
    // 2026): Manual runs it straight from the lesson. Adding it is its own
    // button, with the same plan cap as the collection library.
    const practisePhrases = () => navigate(`/practice?lesson=${lesson.drillTopic}`);
    const addCollection = async () => {
        setOpening(true);
        try {
            await premadeApi.add([lesson.drillTopic]);
            setAdded(true);
            toast.success('Added to your phrases');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Could not add the collection.');
        } finally {
            setOpening(false);
        }
    };

    return (
        <div className="max-w-2xl mx-auto px-4 py-6 md:py-10" data-testid="sound-lesson">
            <button type="button" onClick={() => navigate(-1)}
                    className="inline-flex items-center gap-1 text-xs text-stone-500 hover:text-stone-700 mb-4">
                <ArrowLeft className="h-3.5 w-3.5" /> Back
            </button>
            {/* "ou vs u": the vs in green so the versus stands out (Leanne). */}
            <h1 className="text-2xl font-semibold text-foreground mb-4">
                {/* On a phone the sounds go on their own line (Leanne). */}
                <span className="block sm:inline">{lesson.titlePrefix}</span>{' '}
                {lesson.versus
                    ? <span className="whitespace-nowrap">{lesson.versus[0]} <span className="text-primary font-bold">vs</span> {lesson.versus[1]}</span>
                    : <span className="whitespace-nowrap">{lesson.titleMain}</span>}
            </h1>
            {/* Why it is hard for English speakers (Leanne, 26 Oct 2026). */}
            {lesson.whyHard && (
                <div className="mb-6 rounded-xl border border-primary/15 bg-primary/5 p-4">
                    <p className={`${SUBHEAD} mb-1`}>Why it's tricky</p>
                    <p className="text-sm text-stone-700 leading-snug">{lesson.whyHard}</p>
                </div>
            )}

            <Section step={1} title="Listen" subtitle="Hear the difference.">
                <div className="space-y-3 mb-5">
                    {lesson.hear.map(g => (
                        <div key={g.label}>
                            <div className="flex items-center justify-between gap-3 mb-1.5">
                                <p className="text-sm font-semibold text-stone-600">{g.heading || `Sound: ${g.label}`}</p>
                                <PlayAll words={g.words} />
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                                {g.words.map(w => <Word key={w} word={w} phonetic={phon[w]} />)}
                            </div>
                        </div>
                    ))}
                    <div className="pt-4">
                        <div className="flex items-center justify-between gap-3 mb-1.5">
                            <p className="text-sm font-semibold text-stone-600">Comparisons: {lesson.pairLabels.join(' / ')}</p>
                            <PlayAll words={lesson.pairs.flat()} />
                        </div>
                        <div className="space-y-2">
                            {lesson.pairs.map(set => (
                                <div key={set[0]} className="flex flex-wrap items-center gap-2">
                                    {set.map((w, i) => (
                                        <span key={w} className="inline-flex items-center gap-2">
                                            {i > 0 && <span className="text-stone-400 text-xs">/</span>}
                                            <Word word={w} phonetic={phon[w]} />
                                        </span>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <ListeningGame pairs={lesson.pairs} />
            </Section>

            <Section step={2} title="Repeat & compare" subtitle="How to pronounce">
                <ul className="space-y-1.5 mb-4">
                    {lesson.make.map(m => (
                        <li key={m.label} className="text-sm text-stone-700">
                            <span className="font-semibold">{m.label}:</span> {m.text}
                        </li>
                    ))}
                </ul>
                {(lesson.mistakes || []).length > 0 && (
                    <div className="mb-5">
                        <p className={`${SUBHEAD} mb-1`}>Common mistakes</p>
                        <ul className="list-disc pl-5 space-y-1">
                            {lesson.mistakes.map(m => <li key={m} className="text-sm text-stone-700 leading-snug">{m}</li>)}
                        </ul>
                    </div>
                )}
                <RepeatCompare pairs={lesson.pairs} phon={phon} />
            </Section>

            <Section step={3} title="Practise phrases" subtitle={`${lesson.practiseLabel}.`}>
                <div className="flex flex-wrap gap-3">
                    {/* Same look as the Dashboard's main button (Leanne). */}
                    <Button className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground font-bold shadow-md px-6" onClick={practisePhrases}>
                        <Play className="h-5 w-5 mr-1" />
                        Start Practice Session
                    </Button>
                    {added ? (
                        <span className="inline-flex items-center gap-1 text-sm text-emerald-700 font-medium px-2">
                            <Check className="h-4 w-4" /> In your phrases
                        </span>
                    ) : (
                        <Button variant="outline" className="rounded-full border-primary text-primary" onClick={addCollection} disabled={opening}>
                            {opening ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
                            Add collection to my phrases
                        </Button>
                    )}
                </div>
            </Section>
        </div>
    );
}
